import React, { useState } from 'react';

import { useDispatch } from 'react-redux';

import { FormHelperText } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';

import { addNotificationAction } from '../../action/notifications/notifications';
import { setHrisIntegrationResponsesDirty } from '../../hris-integration-response/actions/set-hris-integration-responses-dirty';

import * as Api from '../../api/api';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';

import { isEmail } from '../../util/string-validators';
import { EmailSupportLink } from '../../widgets/email-support-link';

export const SyncWithInovaDialog = ({ orgId, onClose }: { orgId: number; onClose: () => void }) => {
  const dispatch = useDispatch();

  const [repName, setRepName] = useState<string>('');
  const [repEmail, setRepEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notificationSuccess, setNotificationSuccess] = useState<boolean>(true);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setRepName(e.target.value);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;

    setRepEmail(email);
    setEmailError(!!email && !isEmail(email));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const response = await Api.post(`/hris-integration-response/inova-sync-request`, {
      body: JSON.stringify({ orgId, repName, repEmail }),
    });
    const { success } = await response.json();
    setSubmitting(false);
    setNotificationSuccess(success);
    if (success) {
      dispatch(
        addNotificationAction({
          key: `inova-sync-request-success-${orgId}`,
          message:
            'Your request has been sent successfully. We will reach out when the setup is complete.',
        }),
      );
      dispatch(setHrisIntegrationResponsesDirty(orgId));
      onClose();
    }
  };

  const missingField = !repEmail || !repName;

  return (
    <UiDialog onClose={onClose} title="Sync with Inova">
      <UiDialog.Content>
        <div className="typo-title">
          We will set up the sync on your behalf. Please provide your Inova representative&apos;s
          email below and we will notify you once the set up is complete.
        </div>
        <div className="u-display-flex u-margin-0Auto u-justifyContent-spaceBetween u-width-90 u-margin-bottom-lg">
          <TextField
            className="u-width-45"
            label="Representative Name"
            value={repName}
            onChange={handleNameChange}
            disabled={!notificationSuccess}
            required
          />
          <div className="u-width-45">
            <TextField
              className="u-width-100"
              label="Representative Email"
              value={repEmail}
              onChange={handleEmailChange}
              disabled={!notificationSuccess}
              required
            />
            {emailError && (
              <FormHelperText className="u-color-red">
                Please enter a valid email address
              </FormHelperText>
            )}
          </div>
        </div>
        {!notificationSuccess && (
          <div>
            We encountered an error initiating your sync with Inova. Please send an email to{' '}
            <EmailSupportLink /> with your Inova representative&apos;s name and email to request
            your sync to be set up, or refresh the page and try again.
          </div>
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="secondary" onClick={onClose} disabled={submitting}>
          {!notificationSuccess ? 'Close' : 'Cancel'}
        </UiButton>
        <UiButton
          onClick={handleSubmit}
          disabled={emailError || missingField || !notificationSuccess}
          loading={submitting}
        >
          Send Request
        </UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
};
